/* eslint-disable react/require-default-props */
import React, { useCallback, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { localStorage } from '@medflex/utils/browserStorage';
import { HarmonizerMessageRoles, RequestCallTypes, RequestValueKeys } from '@medflex/shared-types';
import CopyButton from '../../CopyButton';
import InputCheckbox from '../../InputCheckbox';
import InquiryCallDetailsMessages from './InquiryCallDetailsMessages';
import PlayerAudioWave from '../../VoiceMessage/PlayerAudioWave';

const LS = localStorage('inbox_inquiry_value_call_details_todos');

const InquiryCallDetails = ({ requestId, summary, todos, messages, files, onFetchAudioSource }) => {
  const { t } = useTranslation('components', { keyPrefix: 'PamInquiry.InquiryCallDetails' });
  const audioRef = useRef();
  const [currentValues, setCurrentValues] = useState(LS.get(requestId) || []);

  const handleOnChange = useCallback(
    ({ target }) => {
      const currentValues = LS.get(requestId) || [];
      const updatedValues = target.checked
        ? [...currentValues, target.value]
        : currentValues.filter(todo => todo !== target.value);
      LS.set(requestId, updatedValues);

      setCurrentValues(updatedValues);
    },
    [requestId],
  );

  const handleAudioSkipSeconds = useCallback(
    seconds => {
      audioRef.current.setTime(seconds);
      audioRef.current.play();
    },
    [audioRef],
  );

  const [originalMessages, translatedMessages] = useMemo(() => {
    const original = [];
    const translated = [];

    if (!messages?.[0]?.translation) {
      return [messages];
    }

    messages.forEach(message => {
      const { content, translation, ...rest } = message;
      original.push({ ...rest, content });
      if (translation) {
        translated.push({ ...rest, content: translation });
      }
    });

    return [original, translated];
  }, [messages]);

  if (!todos?.length && !summary && !messages?.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-8">
      {summary !== '' && (
        <div className="rounded-md shadow-md px-2 py-4 bg-blue-100">
          <div className="text-lg font-semibold mb-1 flex justify-between">
            <span>{t('summary_title')}</span>
            <span>
              <CopyButton value={summary} />
            </span>
          </div>
          <div>{summary || t('summary_pending')}</div>
        </div>
      )}
      {todos && (
        <div>
          <div className="text-lg font-semibold leading-5 mb-6">{t('todos_title')}</div>
          <ul className="flex flex-col gap-1">
            {todos.map((todo, i) => (
              <li key={todo}>
                <label htmlFor={`todo_${i}`} className="flex">
                  <InputCheckbox
                    id={`todo_${i}`}
                    name={`${RequestValueKeys.CALL}_todos`}
                    value={todo}
                    checked={currentValues.includes(todo)}
                    onChange={handleOnChange}
                  />
                  <span className="ml-2">{todo}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
      {messages && (
        <div>
          <div className="text-lg font-semibold mb-6">{t('messages.title')}</div>
          {files && files.length > 0 && (
            <PlayerAudioWave
              src={() => onFetchAudioSource(files[0], RequestCallTypes.CONVERSATIONAL)}
              ref={audioRef}
              className="mb-2 @md:mb-0 @md:relative @md:z-20 bg-gradient-to-b from-white via-white to-transparent"
            />
          )}
          <div className="flex flex-col overflow-y-auto max-h-[450px] px-4 @md:py-10 @md:pb-4 @md:-mt-6 @md:relative @md:z-10">
            <InquiryCallDetailsMessages
              original={originalMessages}
              translated={translatedMessages}
              onAudioSkipSeconds={handleAudioSkipSeconds}
            />
          </div>
        </div>
      )}
    </div>
  );
};

InquiryCallDetails.propTypes = {
  requestId: PropTypes.string,
  summary: PropTypes.string,
  todos: PropTypes.arrayOf(PropTypes.string),
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(Object.values(HarmonizerMessageRoles)),
      content: PropTypes.string,
      translation: PropTypes.string,
      createdAt: PropTypes.string,
      timeInCallSeconds: PropTypes.number,
    }),
  ),
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      mediaType: PropTypes.string,
      title: PropTypes.string,
      id: PropTypes.string,
      bytes: PropTypes.number,
      detectedMediaType: PropTypes.string,
    }),
  ),
  onFetchAudioSource: PropTypes.func,
};

export default InquiryCallDetails;
